import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';

import { LoadingOverlayComponent } from '@components2/shell/loading-overlay/loading-overlay.component';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { filter, map } from 'rxjs';

import { ContentService } from '@services/content.service';
import { DarkModeService } from '@services/dark-mode.service';
import { MatomoService } from '@services/matomo.service';
import { MappedParams, NavService } from '@services/nav.service';

import { Shell2Component } from './components2/shell/shell.component';

@Component({
  standalone: true,
  imports: [RouterModule, LoadingOverlayComponent, Shell2Component, ToastModule],
  selector: 'bk-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  matomoService = inject(MatomoService);
  darkModeService = inject(DarkModeService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  navService = inject(NavService);
  destroyRef = inject(DestroyRef);
  texts = inject(ContentService).texts;

  messageService = inject(MessageService);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.route;
          const routeParamsMap = new Map<string, any>();
          const queryParamsMap = new Map<string, any>();

          // Iterate through all child routes to get all params and query params
          while (child.firstChild) {
            const paramMap = child.firstChild.snapshot.paramMap;
            const queryParamMap = child.firstChild.snapshot.queryParamMap;

            paramMap.keys.forEach((key) => {
              const value = paramMap.get(key);
              if (value) {
                routeParamsMap.set(key, value);
              }
            });

            queryParamMap.keys.forEach((key) => {
              const value = queryParamMap.get(key);
              if (value) {
                queryParamsMap.set(key, value);
              }
            });

            child = child.firstChild;
          }

          const mappedParams: MappedParams = {
            customerId: routeParamsMap.get('customerId') || '',
            invoiceNumber: routeParamsMap.get('invoiceNumber') || '',
            inquiryNumber: routeParamsMap.get('inquiryNumber') || '',
            inquiryRowNumber: routeParamsMap.get('inquiryRowNumber') || '',
            callOffNumber: routeParamsMap.get('callOffNumber') || '',
            sourceSystemCode: routeParamsMap.get('sourceSystemCode') || '',
            orderNumber: routeParamsMap.get('orderNumber') || '',
            orderRowNumber: routeParamsMap.get('orderRowNumber') || '',
            warehouseCode: routeParamsMap.get('warehouseCode') || '',
            selectedId: routeParamsMap.get('selectedId') || '',
            deliveryNoteId: routeParamsMap.get('deliveryNoteId') || '',
            traceId: routeParamsMap.get('traceId') || '',
            eudr: routeParamsMap.get('edur') || '',
            eudrPreview: routeParamsMap.get('eudrPreview') || '',
            sap: routeParamsMap.get('sap') || '',
            accountManagement: routeParamsMap.get('accountmanagement') || '',
            q: queryParamsMap.get('q') || '',
            oqOrders: queryParamsMap.get('oqOrders') || '',
          };

          const queryParams = Object.fromEntries(queryParamsMap);

          return [mappedParams, queryParams];
        }),
      )
      .subscribe(([mappedParams, queryParams]) => {
        const currentParams = this.navService.params$.value;
        const params = { ...currentParams, ...mappedParams, ...queryParams };
        this.navService.params$.next(params);
      });
  }
}
